/**
 * ユーザー管理(設定)。ログインできるユーザーと3ロール(admin/operator/viewer)を管理する。
 * 管理者・閲覧者は Googleアカウントでログインする(2026-09-15)。追加は「招待」: メールアドレスとロールを登録し、
 * 本人がそのGoogleアカウントで初めてログインしたときに users へ登録される(userRepo.claimInvite)。
 * スタッフ(operator)のログイン方式は保留(P-12)なので、招待できるのは管理者・閲覧だけ。
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal from '@core/components/Modal';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { SearchBox, SortTh, matchKeyword, sortRows, useSort } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import { useAuth } from '@core/auth/AuthProvider';
import { ROLE_LABELS, type Operator, type Role, type UserDoc } from '@core/lib/types';
import { createInvite, deleteInvite, fetchInvites, fetchUsers, updateUser, type InviteRole, type UserInvite } from './userRepo';
import { fetchOperators, linkOperatorUid } from './operatorRepo';

type SortKey = 'name' | 'email' | 'role' | 'op' | 'active';

const ROLE_BADGE: Record<Role, string> = {
  admin: 'role-approver',
  operator: 'role-member',
  viewer: 'role-accounting',
};

const ROLES = Object.keys(ROLE_LABELS) as Role[];
const ROLE_OPTIONS = ROLES.map((key) => ({ key, label: ROLE_LABELS[key] }));

const operatorNameOf = (operators: Operator[], opId: string | null) => (opId ? (operators.find((o) => o.id === opId)?.name ?? opId) : null);

export default function SettingsUsersPage() {
  const toast = useToast();
  const { user: myself, refreshUser } = useAuth();
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [invites, setInvites] = useState<UserInvite[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [roles, setRoles] = useState<Set<Role>>(() => new Set());
  const { sort, toggle } = useSort<SortKey>();
  const [editing, setEditing] = useState<UserDoc | null>(null);
  const [inviting, setInviting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [u, o, inv] = await Promise.all([fetchUsers(), fetchOperators(), fetchInvites()]);
      setUsers(u);
      setOperators(o);
      setInvites(inv);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const rows = users.filter((u) => matchesFilter(roles, u.role)).filter((u) => matchKeyword(keyword, u.name, u.email));
    return sortRows(rows, sort, (u, key) => {
      switch (key) {
        case 'name':
          return u.name;
        case 'email':
          return u.email;
        case 'role':
          return ROLES.indexOf(u.role);
        case 'op':
          return operatorNameOf(operators, u.op_id);
        case 'active':
          return u.active ? 1 : 0;
      }
    });
  }, [users, operators, roles, keyword, sort]);

  const pending = useMemo(
    () => invites.filter((i) => matchesFilter(roles, i.role)).filter((i) => matchKeyword(keyword, i.name, i.email)),
    [invites, roles, keyword],
  );

  async function cancelInvite(inv: UserInvite) {
    if (!confirm(`${inv.name}(${inv.email})の追加を取り消します。よろしいですか?`)) return;
    try {
      await deleteInvite(inv.id);
      toast('取り消しました');
      void reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : '取り消しに失敗しました');
    }
  }

  return (
    <div className="screen">
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          ユーザー一覧
          <span className="hint">
            {filtered.length}名{pending.length > 0 && ` ・ 初回ログイン待ち ${pending.length}名`}
          </span>
          <button className="btn-line" style={{ marginLeft: 'auto' }} onClick={() => setInviting(true)}>
            ＋ユーザーを追加
          </button>
        </h3>

        <div className="toolbar" style={{ marginBottom: 12 }}>
          <div className="tool-filter">
            <MultiSelect label="ロール" options={ROLE_OPTIONS} value={roles} onChange={setRoles} />
            <SearchBox value={keyword} onChange={setKeyword} placeholder="名前・メールで検索" />
          </div>
        </div>

        {loading ? (
          <div className="empty">読み込み中…</div>
        ) : filtered.length === 0 && pending.length === 0 ? (
          <div className="empty">該当するユーザーがいません</div>
        ) : (
          <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <SortTh label="氏名" sortKey="name" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="メールアドレス" sortKey="email" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="ロール" sortKey="role" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="オペレーター紐付け" sortKey="op" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="状態" sortKey="active" sort={sort} onSort={toggle} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id}>
                  <td>
                    <b>{u.name}</b>
                    {u.id === myself?.id && <span className="hint">(自分)</span>}
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <span className={`badge ${ROLE_BADGE[u.role]}`}>{ROLE_LABELS[u.role]}</span>
                  </td>
                  <td>{operatorNameOf(operators, u.op_id) ?? '—'}</td>
                  <td>
                    <span className={`badge ${u.active ? 'b-ok' : 'b-ng'}`}>{u.active ? '有効' : '無効'}</span>
                  </td>
                  <td>
                    <button className="txn-link" onClick={() => setEditing(u)}>
                      編集
                    </button>
                  </td>
                </tr>
              ))}
              {pending.map((i) => (
                <tr key={`invite-${i.id}`}>
                  <td>
                    <b>{i.name}</b>
                  </td>
                  <td>{i.email}</td>
                  <td>
                    <span className={`badge ${ROLE_BADGE[i.role]}`}>{ROLE_LABELS[i.role]}</span>
                  </td>
                  <td>—</td>
                  <td>
                    <span className="badge b-pend">初回ログイン待ち</span>
                  </td>
                  <td>
                    <button className="txn-link" onClick={() => void cancelInvite(i)}>
                      取消
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {inviting && (
        <InviteModal
          users={users}
          invitedBy={myself?.id ?? ''}
          onClose={() => setInviting(false)}
          onSaved={() => {
            setInviting(false);
            void reload();
          }}
        />
      )}

      {editing && (
        <UserEditModal
          target={editing}
          operators={operators}
          myselfId={myself?.id ?? ''}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
            void refreshUser();
          }}
        />
      )}
    </div>
  );
}

/** ユーザーの追加(Googleアカウントのメールアドレスで招待する) */
function InviteModal({
  users,
  invitedBy,
  onClose,
  onSaved,
}: {
  users: UserDoc[];
  invitedBy: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('viewer');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await createInvite({ email, name, role, invitedBy }, users);
      toast('追加しました。本人がこのGoogleアカウントでログインすると使えるようになります');
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : '追加に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(520px, 94vw)">
      <h3>ユーザーを追加</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-grid" style={{ marginTop: 14 }}>
          <div>
            <label>氏名 *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label>Googleアカウントのメールアドレス *</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
          </div>
          <div>
            <label>ロール</label>
            <select value={role} onChange={(e) => setRole(e.target.value as InviteRole)}>
              <option value="admin">{ROLE_LABELS.admin}(admin)</option>
              <option value="viewer">{ROLE_LABELS.viewer}(viewer)</option>
            </select>
          </div>
        </div>
        <div className="hint" style={{ marginTop: 10 }}>本人がこのGoogleアカウントで初めてログインしたときに登録されます</div>
        <div className="modal-actions">
          <button className="btn-green" type="submit" disabled={busy}>
            追加する
          </button>
          <button className="btn-line" type="button" onClick={onClose}>
            キャンセル
          </button>
        </div>
      </form>
    </Modal>
  );
}

function UserEditModal({
  target,
  operators,
  myselfId,
  onClose,
  onSaved,
}: {
  target: UserDoc;
  operators: Operator[];
  myselfId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(target.name);
  const [role, setRole] = useState<Role>(target.role);
  const [active, setActive] = useState(target.active);
  const [opId, setOpId] = useState(target.op_id ?? '');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const newOpId = role === 'operator' && opId ? opId : null;
      if (target.id === myselfId && (role !== 'admin' || !active)) {
        throw new Error('自分自身のロール変更・無効化はできません(締め出し防止)');
      }
      await updateUser(target.id, { name, role, active, op_id: newOpId });
      // 紐付けの変更を operators 側(uid)にも反映する
      const before = target.op_id;
      if (before && before !== newOpId) await linkOperatorUid(before, null);
      if (newOpId) await linkOperatorUid(newOpId, target.id);
      toast('保存しました');
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(520px, 94vw)">
      <h3>{target.name}の編集</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-grid" style={{ marginTop: 14 }}>
          <div>
            <label>氏名 *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label>メールアドレス(変更不可)</label>
            <input type="email" value={target.email} disabled />
          </div>
          <div>
            <label>ロール</label>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {(Object.entries(ROLE_LABELS) as [Role, string][]).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}({k})
                </option>
              ))}
            </select>
          </div>
          {role === 'operator' && (
            <div>
              <label>オペレーター紐付け(本人成績の表示に使用)</label>
              <select value={opId ?? ''} onChange={(e) => setOpId(e.target.value)}>
                <option value="">未紐付け</option>
                {operators.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}({o.op_code})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label>状態</label>
            <select value={active ? '1' : '0'} onChange={(e) => setActive(e.target.value === '1')}>
              <option value="1">有効</option>
              <option value="0">無効(ログイン不可)</option>
            </select>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-green" type="submit" disabled={busy}>
            保存する
          </button>
          <button className="btn-line" type="button" onClick={onClose}>
            キャンセル
          </button>
        </div>
      </form>
    </Modal>
  );
}
